import {
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  IUiAlertContent,
  ToastService,
  TUiAlertTypes,
} from '@irembo-andela/irembogov3-common';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { environment } from '../../../../../../environments/environment';
import {
  emailOrPhoneValidator,
  removeExtraWhiteSpaces,
} from '../../../../../core/utils/forms-utils';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { ClientService } from '../../../../../core/services/client.service';
import { UsersService } from '../../../../../core/services/users.service';
import { RequestTypeEnum } from '../../../../../core/models/request-type.enum.model';

@Component({
  selector: 'irembogov-client-manager-invite-super-service-provider',
  templateUrl: './client-manager-invite-super-service-provider.component.html',
})
export class ClientManagerInviteSuperServiceProviderComponent
  implements OnInit
{
  @Output() closeForm: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() newSuperServiceProviderFormSubmitted: EventEmitter<any> =
    new EventEmitter<any>();
  @ViewChild('errorMessageElement') errorMessageElement!: ElementRef;
  formData!: FormData;

  isAssigning = false;
  isLoading = false;
  showFormError = false;
  formAlertContent: IUiAlertContent = {
    title: '',
    message: '',
    type: 'warning',
  };

  addNewSuperServiceProvider: FormGroup = new FormGroup({
    firstName: new FormControl<string>('', [
      Validators.required,
      Validators.pattern(environment.personNameRegex),
    ]),
    lastName: new FormControl<string>('', [
      Validators.required,
      Validators.pattern(environment.personNameRegex),
    ]),
    username: new FormControl<string>('', [
      Validators.required,
      emailOrPhoneValidator(),
    ]),
  });

  private unsubscribe$ = new Subject<void>();
  constructor(
    private clientService: ClientService,
    private usersService: UsersService,
    private toastService: ToastService
  ) {}
  ngOnInit(): void {
    this.formData = new FormData();
    this.addNewSuperServiceProvider.reset();
    this.initiateUsernameSubscription();
  }

  initiateUsernameSubscription() {
    this.addNewSuperServiceProvider.controls['username'].valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        filter(() => {
          if (
            !this.addNewSuperServiceProvider.controls['username'].valid &&
            this.isAssigning
          ) {
            this.addNewSuperServiceProvider.controls['firstName'].reset();
            this.addNewSuperServiceProvider.controls['lastName'].reset();
            this.isAssigning = false;
          }
          return this.addNewSuperServiceProvider.controls['username'].valid;
        }),
        switchMap(value => {
          return this.usersService.checkIfIremboUserExists(value).pipe(
            catchError(() => {
              if (this.isAssigning) {
                this.addNewSuperServiceProvider.controls['firstName'].reset();
                this.addNewSuperServiceProvider.controls['lastName'].reset();
                this.isAssigning = false;
              }
              return EMPTY;
            })
          );
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: res => {
          this.isAssigning = true;
          this.addNewSuperServiceProvider.controls['firstName'].setValue(
            res.data.firstName
          );
          this.addNewSuperServiceProvider.controls['lastName'].setValue(
            res.data.lastName
          );
        },
      });
  }

  cancelForm() {
    this.isLoading = false;
    this.closeForm.emit(true);
  }

  onCreateSuperServiceProvider() {
    if (!this.validateForm(this.addNewSuperServiceProvider)) {
      return;
    }

    this.updateFormValidationErrorMessageContent(false);
    this.isLoading = true;

    const req: any = {
      firstName: removeExtraWhiteSpaces(
        this.addNewSuperServiceProvider.controls['firstName'].value
      ),
      lastName: removeExtraWhiteSpaces(
        this.addNewSuperServiceProvider.controls['lastName'].value
      ),
      username: this.addNewSuperServiceProvider.controls['username'].value,
      requestType: this.isAssigning
        ? RequestTypeEnum.ASSIGN
        : RequestTypeEnum.CREATE,
    };

    this.clientService
      .initiateRequestToCreateQAReviewer(req, 'super-service-provider-request')
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: () => {
          const newServiceManager: any = {
            firstName: req.firstName,
          } as any;

          this.newSuperServiceProviderFormSubmitted.emit(newServiceManager);
          this.toastService.show({
            body: this.isAssigning
              ? 'Super Service Provider role assigned successfully'
              : `Created new super service provider success!`,
            type: 'success',
          });
        },
        error: error => {
          const errorMessage = error.responseMessage
            ? error.error.responseMessage
            : this.isAssigning
            ? 'Failed to assign Super Service Provider role'
            : 'Could not initiate new Super Service Provider';

          this.updateFormValidationErrorMessageContent(
            true,
            'danger',
            this.isAssigning
              ? 'Failed to assign Super Service Provider role'
              : 'Creating new Super Service Provider failed!',
            errorMessage
          );
        },
      });
  }

  private validateForm(form: FormGroup): boolean {
    if (form.invalid) {
      this.updateFormValidationErrorMessageContent(
        true,
        'danger',
        'Invalid fields!',
        'Please ensure you have filled in all the required fields.'
      );
      return false;
    }
    return true;
  }

  updateFormValidationErrorMessageContent(
    show: boolean,
    type: TUiAlertTypes = 'warning',
    title = '',
    message = ''
  ) {
    this.showFormError = show;
    this.formAlertContent = { type, title, message };
    if (show) {
      this.errorMessageElement.nativeElement.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }
}
